/**
 * Buff Beauty — Klaviyo Back in Stock
 *
 * Client-side BIS subscriptions + email/SMS marketing consent + custom event.
 * Fill KLAVIYO_SMS_LIST_ID with the Klaviyo list ID before launch.
 */
const KLAVIYO_COMPANY_ID = 'Swhktw';
const KLAVIYO_REVISION = '2026-07-15'; // current stable per Klaviyo OpenAPI
const KLAVIYO_SMS_LIST_ID = ''; // TODO: fill from Klaviyo account before launch
const KLAVIYO_BIS_URL = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;
const KLAVIYO_SUBSCRIPTIONS_URL = `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;
const KLAVIYO_EVENTS_URL = `https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_COMPANY_ID}`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MODAL_ID = 'modal-back-in-stock';

class BuffBackInStock {
  constructor() {
    this.modal = document.getElementById(MODAL_ID);
    this.form = this.modal?.querySelector('[data-buff-bis-form]');
    if (!this.modal || !this.form) return;

    this.fieldsEl = this.form.querySelector('[data-buff-bis-fields]');
    this.successEl = this.form.querySelector('[data-buff-bis-success]');
    this.introEl = this.form.querySelector('[data-buff-bis-intro]');
    this.productTitleEl = this.form.querySelector('[data-buff-bis-product-title]');
    this.consentEl = this.form.querySelector('[data-buff-bis-consent]');
    this.submitBtn = this.form.querySelector('[data-buff-bis-submit]');
    this.nameInput = this.form.querySelector('[name="first_name"]');
    this.emailInput = this.form.querySelector('[name="email"]');
    this.phoneInput = this.form.querySelector('[name="phone"]');
    this.iti = null;

    this.variantId = null;
    this.productTitle = '';
    this.productImage = '';
    this.productUrl = '';
    this.lastTrigger = null;
    this.strings = window.buffBackInStockStrings || {};

    this.onPhoneInput = this.onPhoneInput.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onOpenEvent = this.onOpenEvent.bind(this);
    this.onModalClosed = this.onModalClosed.bind(this);

    this.initPhoneInput();
    this.phoneInput?.addEventListener('input', this.onPhoneInput);
    this.phoneInput?.addEventListener('countrychange', this.onPhoneInput);
    this.form.addEventListener('submit', this.onSubmit);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('buff:back-in-stock:open', this.onOpenEvent);
    this.modal.addEventListener('transitionend', this.onModalClosed);
  }

  initPhoneInput() {
    if (!this.phoneInput || typeof window.intlTelInput !== 'function') return;

    // WithUtils bundle already includes libphonenumber — formats as you type + E.164 on getNumber()
    this.iti = window.intlTelInput(this.phoneInput, {
      initialCountry: 'us',
      countryOrder: ['us'],
      nationalMode: true,
      formatAsYouType: true,
      formatOnDisplay: true,
      strictMode: true,
      autoPlaceholder: 'aggressive',
      containerClass: 'buff-bis-iti',
    });
  }

  stringsOr(key, fallback) {
    return this.strings[key] || fallback;
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-buff-bis], .buff-bis-trigger');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    this.openFromTrigger(trigger);
  }

  onOpenEvent(event) {
    const detail = event.detail || {};
    this.open({
      variantId: detail.variantId,
      productTitle: detail.productTitle || '',
      productImage: detail.productImage || '',
      productUrl: detail.productUrl || '',
      trigger: detail.trigger || null,
    });
  }

  openFromTrigger(trigger) {
    let variantId = trigger.dataset.variantId || '';
    let productTitle = trigger.dataset.productTitle || '';
    let productImage = trigger.dataset.productImage || '';
    let productUrl = trigger.dataset.productUrl || '';

    if (!variantId) {
      const form = trigger.closest('form') || trigger.closest('product-form')?.querySelector('form');
      // Upsells use <select name="id">; main ATC uses <input name="id">
      const idField = form?.querySelector('[name="id"]');
      if (idField?.value) variantId = idField.value;
    }

    if (!productTitle) {
      productTitle =
        trigger.dataset.productTitle ||
        document.querySelector('.product-info .product-title, .product-modal__title, [data-product-title]')?.textContent?.trim() ||
        document.title;
    }

    if (!productImage) {
      productImage =
        trigger.dataset.productImage ||
        document.querySelector('[data-product-image]')?.getAttribute('data-product-image') ||
        '';
    }

    if (!productUrl) {
      productUrl = trigger.dataset.productUrl || window.location.href;
    }

    this.open({ variantId, productTitle, productImage, productUrl, trigger });
  }

  open({ variantId, productTitle, productImage, productUrl, trigger }) {
    if (!variantId) {
      console.warn('[Buff BIS] Missing variant ID');
      return;
    }

    this.variantId = String(variantId);
    this.productTitle = productTitle || '';
    this.productImage = productImage || '';
    this.productUrl = productUrl || window.location.href;
    this.lastTrigger = trigger || null;
    this.resetFormUI();

    if (this.productTitleEl) {
      this.productTitleEl.textContent = this.productTitle || 'this product';
    }
    if (this.introEl) {
      this.introEl.hidden = false;
    }

    if (window.Woolman?.ModalsAndDrawers?.showModalOrDrawer) {
      Woolman.ModalsAndDrawers.showModalOrDrawer(MODAL_ID);
    } else {
      this.modal.classList.add('is-open', 'initialized');
    }

    requestAnimationFrame(() => {
      (this.nameInput || this.emailInput)?.focus();
    });
  }

  onModalClosed() {
    if (this.modal.classList.contains('is-open')) return;
    if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      this.lastTrigger.focus();
    }
  }

  onPhoneInput() {
    this.clearError('phone');
  }

  resetFormUI() {
    this.clearAllErrors();
    if (this.fieldsEl) this.fieldsEl.hidden = false;
    if (this.successEl) this.successEl.hidden = true;
    this.form.reset();
    if (this.iti) {
      this.iti.setCountry('us');
      this.iti.setNumber('');
    }
    if (this.consentEl) this.consentEl.hidden = false;
    this.setLoading(false);
  }

  clearError(key) {
    const el = this.form.querySelector(`[data-buff-bis-error="${key}"]`);
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  clearAllErrors() {
    this.form.querySelectorAll('[data-buff-bis-error]').forEach((el) => {
      el.hidden = true;
      el.textContent = '';
    });
  }

  showError(key, message) {
    const el = this.form.querySelector(`[data-buff-bis-error="${key}"]`);
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;
    this.submitBtn.classList.toggle('loading', isLoading);
    this.submitBtn.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    this.submitBtn.disabled = isLoading;
  }

  phoneHasDigits() {
    return Boolean((this.phoneInput?.value || '').replace(/\D/g, '').length);
  }

  async validate() {
    this.clearAllErrors();

    const firstName = (this.nameInput?.value || '').trim();
    const email = (this.emailInput?.value || '').trim();
    // Consent is covered by the on-form notice (“By submitting…”) — no separate checkbox.
    const consented = true;
    let phone = '';
    let ok = true;

    if (!email || !EMAIL_RE.test(email)) {
      this.showError('email', this.stringsOr('errorEmail', 'Please enter a valid email'));
      ok = false;
    }

    if (this.phoneHasDigits()) {
      if (this.iti) {
        if (!this.iti.isValidNumber()) {
          this.showError('phone', this.stringsOr('errorPhone', 'Please enter a valid phone number'));
          ok = false;
        } else {
          phone = this.iti.getNumber() || ''; // E.164 e.g. +14849839606
        }
      } else {
        this.showError('phone', this.stringsOr('errorPhone', 'Please enter a valid phone number'));
        ok = false;
      }
    }

    return { ok, firstName, email, phone, consented };
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitBtn?.disabled) return;

    const { ok, firstName, email, phone, consented } = await this.validate();
    if (!ok) return;

    const wantsSms = Boolean(phone && consented);
    this.setLoading(true);

    try {
      const bisRes = await this.subscribeBackInStock({ firstName, email, phone, wantsSms });
      if (!bisRes.ok && bisRes.status !== 202) {
        // Klaviyo silently no-ops duplicates; treat ambiguous client errors as success only when status is 202
        this.showError('form', this.mapApiErrors(bisRes.errors) || this.stringsOr('errorGeneric', 'Something went wrong. Please try again.'));
        this.setLoading(false);
        return;
      }

      // Non-blocking custom event for flows / analytics — never fail the waitlist UX
      this.trackSubscribedEvent({ firstName, email, phone }).catch((err) => {
        console.warn('[Buff BIS] Events API request failed', err);
      });

      if (consented) {
        await this.subscribeMarketingConsent({ firstName, email, phone, wantsSms });
      }

      this.showSuccess();
    } catch (err) {
      console.error('[Buff BIS]', err);
      this.showError('form', this.stringsOr('errorGeneric', 'Something went wrong. Please try again.'));
    } finally {
      this.setLoading(false);
    }
  }

  mapApiErrors(errors) {
    if (!Array.isArray(errors) || !errors.length) return null;
    const detail = errors[0]?.detail || errors[0]?.title || '';
    const lower = String(detail).toLowerCase();
    if (lower.includes('email')) return this.stringsOr('errorEmail', 'Please enter a valid email');
    if (lower.includes('phone')) return this.stringsOr('errorPhone', 'Please enter a valid phone number');
    return this.stringsOr('errorGeneric', 'Something went wrong. Please try again.');
  }

  async subscribeBackInStock({ firstName, email, phone, wantsSms }) {
    // Klaviyo BIS profile only allows email / phone_number — first_name returns 400 invalid field
    const profileAttributes = { email };
    if (phone) profileAttributes.phone_number = phone;

    const channels = wantsSms ? ['EMAIL', 'SMS'] : ['EMAIL'];

    const body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
          channels,
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.variantId}`,
            },
          },
        },
      },
    };

    const res = await fetch(KLAVIYO_BIS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    });

    let errors = null;
    if (!res.ok && res.status !== 202) {
      try {
        const json = await res.json();
        errors = json?.errors || null;
      } catch (_) {
        errors = null;
      }
    }

    return { ok: res.status === 202, status: res.status, errors };
  }

  async subscribeMarketingConsent({ firstName, email, phone, wantsSms }) {
    if (!KLAVIYO_SMS_LIST_ID) {
      console.warn('[Buff BIS] KLAVIYO_SMS_LIST_ID is empty — skipping marketing consent call. Fill in before launch.');
      return;
    }

    const profileAttributes = {
      email,
      subscriptions: {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      },
    };

    if (firstName) profileAttributes.first_name = firstName;

    if (wantsSms && phone) {
      profileAttributes.phone_number = phone;
      profileAttributes.subscriptions.sms = {
        marketing: {
          consent: 'SUBSCRIBED',
        },
      };
    }

    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in stock form',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: KLAVIYO_SMS_LIST_ID,
            },
          },
        },
      },
    };

    try {
      await fetch(KLAVIYO_SUBSCRIPTIONS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });
    } catch (err) {
      console.warn('[Buff BIS] Marketing consent request failed', err);
    }
  }

  async trackSubscribedEvent({ firstName, email, phone }) {
    const profileAttributes = { email };
    if (firstName) profileAttributes.first_name = firstName;
    if (phone) profileAttributes.phone_number = phone;

    const body = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            ProductName: this.productTitle || '',
            ProductImageURL: this.productImage || '',
            ProductURL: this.productUrl || window.location.href,
            VariantId: this.variantId || '',
          },
          metric: {
            data: {
              type: 'metric',
              attributes: {
                name: 'Subscribed to Back in Stock',
              },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
        },
      },
    };

    await fetch(KLAVIYO_EVENTS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    });
  }

  showSuccess() {
    if (this.fieldsEl) this.fieldsEl.hidden = true;
    if (this.successEl) {
      this.successEl.hidden = false;
      this.successEl.focus();
    }
  }
}

/**
 * Configure a product-form submit button as Back in Stock (sold out) or restore ATC.
 */
window.BuffBackInStockUI = {
  setNotifyMe(button, { enable = true, priceText = null } = {}) {
    if (!button) return;
    const label =
      button.querySelector(':scope > span:not(.buy-button-price):not(.buy-bar__button-price):not(.loading-overlay)') ||
      button.querySelector(':scope > span');
    let priceEl = button.querySelector('.buy-button-price, .buy-bar__button-price');

    if (enable) {
      button.type = 'button';
      button.removeAttribute('disabled');
      button.classList.add('buff-bis-trigger');
      button.setAttribute('data-buff-bis', '');
      button.setAttribute('aria-haspopup', 'dialog');
      if (label) label.textContent = window.variantStrings?.notifyMe || 'Join Waitlist';
      if (priceText != null) {
        if (!priceEl) {
          priceEl = document.createElement('span');
          priceEl.className = button.closest('.product-buy-bar, product-buy-bar')
            ? 'buy-bar__button-price'
            : 'buy-button-price';
          label?.after(priceEl);
        }
        // Avoid double separators if price already includes a leading dash
        const clean = String(priceText).replace(/^[\s\-–—]+/, '').trim();
        priceEl.textContent = clean ? `- ${clean}` : '';
      }
    } else {
      button.type = 'submit';
      button.classList.remove('buff-bis-trigger');
      button.removeAttribute('data-buff-bis');
      if (button.getAttribute('aria-haspopup') === 'dialog' && !button.hasAttribute('data-keep-aria-haspopup')) {
        if (document.querySelector('cart-drawer, .drawer-cart')) {
          button.setAttribute('aria-haspopup', 'dialog');
        } else {
          button.removeAttribute('aria-haspopup');
        }
      }
    }
  },
};

document.addEventListener('DOMContentLoaded', () => {
  window.buffBackInStock = new BuffBackInStock();
});
